import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ApiError,
  fetchProgramNewsItems,
  fetchProgramNewsRuns,
  fetchProgramNewsSources,
  runProgramNewsClassification,
  runProgramNewsCollection,
  runStatusOf,
  type ProgramNewsRun,
  type ProgramNewsRunType,
} from "@/services/adminProgramNews";
import { DEFAULT_RUN_POLL_MS, useRunPoller } from "@/hooks/useRunPoller";
import RunButtons from "./RunButtons";
import {
  DEFAULT_CLASSIFY_LIMIT,
  DEFAULT_COLLECT_LIMIT,
  describeRunLimit,
  parseLimit,
} from "./runLimits";
import RunCostLine from "./RunCostLine";
import RunRow from "./RunRow";
import { describeRunCounters, latestClassifyRun } from "./runCounters";
import PanelIntro from "./PanelIntro";
import { PANEL_INTROS } from "./panelIntros";

const RUN_LIMIT = 20;

const LABELS: Record<ProgramNewsRunType, string> = {
  collect: "Collection",
  classify: "Classification",
};

interface RunsPanelProps {
  /** How often a running run is polled; 5 s by default, shorter in tests. */
  pollIntervalMs?: number;
}

/** Put `run` at the top of the list, replacing any earlier copy of the same run. */
function upsertRun(runs: ProgramNewsRun[], run: ProgramNewsRun): ProgramNewsRun[] {
  return runs.some(item => item._id === run._id)
    ? runs.map(item => (item._id === run._id ? run : item))
    : [run, ...runs];
}

function isRunning(run: ProgramNewsRun): boolean {
  return runStatusOf(run) === "running";
}

const RunsPanel = ({ pollIntervalMs = DEFAULT_RUN_POLL_MS }: RunsPanelProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState<ProgramNewsRun[]>([]);
  /** Type of the run whose POST is in flight (before the API answers). */
  const [starting, setStarting] = useState<ProgramNewsRunType | null>(null);
  /** Id of the run being polled, if any. */
  const [pollingId, setPollingId] = useState<string | null>(null);
  const [collectLimit, setCollectLimit] = useState<string>(String(DEFAULT_COLLECT_LIMIT));
  const [classifyLimit, setClassifyLimit] = useState<string>(String(DEFAULT_CLASSIFY_LIMIT));
  /** Verified sources and waiting articles, for the full-run estimates; null until loaded. */
  const [sourceCount, setSourceCount] = useState<number | null>(null);
  const [waitingCount, setWaitingCount] = useState<number | null>(null);
  const { run: polledRun, done } = useRunPoller(pollingId, { intervalMs: pollIntervalMs });
  const finishedIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchProgramNewsRuns({ limit: RUN_LIMIT });
      setRuns(Array.isArray(response.runs) ? response.runs : []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: "Failed to load runs", description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Numbers for the full-run confirmation; a failure just leaves the estimate generic.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchProgramNewsSources({ limit: 1 });
        setSourceCount(res?.counts?.verified ?? null);
      } catch {
        /* estimate stays generic */
      }
      try {
        const res = await fetchProgramNewsItems({ limit: 1 });
        setWaitingCount(res?.counts?.new ?? null);
      } catch {
        /* estimate stays generic */
      }
    })();
  }, []);

  // A run that was already going when the list loaded (page reload mid-run) gets polled too.
  useEffect(() => {
    if (pollingId) return;
    const live = runs.find(isRunning);
    if (live) setPollingId(live._id);
  }, [runs, pollingId]);

  // Keep the table row in step with each poll; on finish, toast the outcome and refetch.
  useEffect(() => {
    if (!polledRun) return;
    setRuns(prev => upsertRun(prev, polledRun));
    if (!done) return;
    if (finishedIdRef.current !== polledRun._id) {
      finishedIdRef.current = polledRun._id;
      const label = LABELS[polledRun.type];
      if (runStatusOf(polledRun) === "failed") {
        toast({
          title: `${label} failed`,
          description: polledRun.error ?? "The run stopped without saying why.",
          variant: "destructive",
        });
      } else {
        toast({
          title: `${label} complete`,
          description: describeRunCounters(polledRun.type, polledRun.counters),
        });
      }
      void load();
    }
    setPollingId(null);
  }, [polledRun, done, toast, load]);

  function adopt(run: ProgramNewsRun) {
    setRuns(prev => upsertRun(prev, run));
    setPollingId(run._id);
  }

  async function start(type: ProgramNewsRunType, full = false) {
    if (starting || runs.some(isRunning)) return;
    setStarting(type);
    const limit =
      type === "collect"
        ? parseLimit(collectLimit, DEFAULT_COLLECT_LIMIT)
        : parseLimit(classifyLimit, DEFAULT_CLASSIFY_LIMIT);
    const params = full ? { full: true } : { limit };
    try {
      const { run } =
        type === "collect"
          ? await runProgramNewsCollection(params)
          : await runProgramNewsClassification(params);
      toast(
        full
          ? { title: `Full ${LABELS[type].toLowerCase()} started` }
          : {
              title: `${LABELS[type]} started`,
              description: describeRunLimit(type, run?.limit ?? limit) ?? undefined,
            }
      );
      if (run?._id) adopt(run);
      else void load();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409) {
        toast({ title: `A ${LABELS[type].toLowerCase()} run is already in progress` });
        const inProgress = err.data?.run;
        if (inProgress && typeof inProgress === "object" && "_id" in inProgress) {
          adopt(inProgress as ProgramNewsRun);
        } else {
          void load();
        }
      } else {
        const message = err instanceof Error ? err.message : String(err);
        toast({ title: `${LABELS[type]} failed`, description: message, variant: "destructive" });
      }
    } finally {
      setStarting(null);
    }
  }

  const liveRun = runs.find(isRunning) ?? null;
  const running: ProgramNewsRunType | null = starting ?? liveRun?.type ?? null;

  return (
    <div className="space-y-4">
      <PanelIntro intro={PANEL_INTROS.runs} />
      <RunButtons
        collectLimit={collectLimit}
        classifyLimit={classifyLimit}
        onCollectLimitChange={setCollectLimit}
        onClassifyLimitChange={setClassifyLimit}
        running={running}
        onCollect={() => void start("collect")}
        onClassify={() => void start("classify")}
        onCollectFull={() => void start("collect", true)}
        onClassifyFull={() => void start("classify", true)}
        sourceCount={sourceCount}
        waitingCount={waitingCount}
      />
      <RunCostLine run={latestClassifyRun(runs)} />
      {loading && runs.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">Loading runs...</div>
      ) : runs.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground">No runs recorded yet.</div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Counters</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map(item => (
                <RunRow key={item._id} run={item} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default RunsPanel;
